import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CompanyApi from "../../../api/companyApi";
import ProjectApi from "../../../api/projectApi";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import Loader from "../../../components/Loader/Loader";
import StandartButton from "../../../components/StandartButton/StandartButton";
import { Company, Project } from "../../../types/Company";
import { getTranslation } from "../../../utils/getTranslation";
import s from "./ProjectFormPage.module.css";
import ProjectFormStepper, { ProjectFormStep } from "./ProjectFormStepper";
import AvailabilityStep from "./steps/AvailabilityStep";
import ContentStep from "./steps/ContentStep";
import CoursesStep from "./steps/CoursesStep";
import PricingStep from "./steps/PricingStep";
import QuizStep from "./steps/QuizStep";
import ReviewStep from "./steps/ReviewStep";
import RulesStep from "./steps/RulesStep";
import SpecialistScopeStep from "./steps/SpecialistScopeStep";
import TagsStep from "./steps/TagsStep";
import { useProjectFormController } from "./useProjectFormController";
import useUnsavedChangesGuard from "./useUnsavedChangesGuard";

type ProjectFormMode = "create" | "edit";

type ProjectFormRoute = {
  mode: ProjectFormMode;
  companyId: string;
  projectId?: string;
};

type LoadState = {
  loading: boolean;
  error: string;
  company: Company | null;
  project: Project | null;
};

type CompanyLoadResponse =
  | Company
  | {
      status?: boolean;
      message?: string;
      company?: Company | null;
    }
  | null;

type ProjectLoadResponse =
  | Project
  | {
      status?: boolean;
      message?: string;
      project?: Project | null;
    }
  | null;

type ProjectFormLoadedContentProps = {
  company: Company;
  mode: ProjectFormMode;
  project: Project | null;
  token: string | null;
};

const parseProjectFormRoute = (pathname: string): ProjectFormRoute | null => {
  const createMatch = pathname.match(/^\/companies\/([^/]+)\/projects\/new$/);
  if (createMatch) {
    return {
      mode: "create",
      companyId: decodeURIComponent(createMatch[1]),
    };
  }

  const editMatch = pathname.match(
    /^\/companies\/([^/]+)\/projects\/([^/]+)\/edit$/,
  );
  if (editMatch) {
    return {
      mode: "edit",
      companyId: decodeURIComponent(editMatch[1]),
      projectId: decodeURIComponent(editMatch[2]),
    };
  }

  return null;
};

const getCompanyFromResponse = (
  response: CompanyLoadResponse,
): Company | null => {
  if (!response) return null;
  const payload = response as {
    status?: boolean;
    company?: Company | null;
  };
  if ("company" in payload || "status" in payload) {
    return payload.status === false ? null : (payload.company ?? null);
  }
  return response as Company;
};

const getProjectFromResponse = (
  response: ProjectLoadResponse,
): Project | null => {
  if (!response) return null;
  const payload = response as {
    status?: boolean;
    project?: Project | null;
  };
  if ("project" in payload || "status" in payload) {
    return payload.status === false ? null : (payload.project ?? null);
  }
  return response as Project;
};

const getResponseMessage = (
  response: CompanyLoadResponse | ProjectLoadResponse,
) => (response && "message" in response ? (response.message ?? "") : "");

const ProjectFormLoadedContent = ({
  company,
  mode,
  project,
  token,
}: ProjectFormLoadedContentProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = mode === "edit";
  const projectTitle = getTranslation(project?.title, "en");
  const initialStep = useMemo(() => {
    const step = new URLSearchParams(location.search).get("step");
    return step === "Quiz" ? "Quiz" : "Content";
  }, [location.search]);
  const [activeStep, setActiveStep] = useState<ProjectFormStep>(initialStep);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [pendingNavigation, setPendingNavigation] = useState<
    null | (() => void)
  >(null);
  const skipUnsavedPromptRef = useRef(false);
  const controller = useProjectFormController({
    mode,
    selectedCompany: company,
    token,
    project,
    onBeforeSuccessfulNavigate: () => {
      skipUnsavedPromptRef.current = true;
    },
  });
  const returnToProjects = useCallback(() => {
    navigate(`/companies?companyId=${company._id}&tab=projects`);
  }, [company._id, navigate]);
  const handleBlockedNavigation = useCallback((retryNavigation: () => void) => {
    setPendingNavigation(() => retryNavigation);
  }, []);
  const requestGuardedNavigation = useUnsavedChangesGuard({
    enabled: controller.isDirty && !skipUnsavedPromptRef.current,
    shouldBlock: () => controller.isDirty && !skipUnsavedPromptRef.current,
    onBlockedNavigation: handleBlockedNavigation,
  });
  const confirmPendingNavigation = () => {
    if (!pendingNavigation) return;

    skipUnsavedPromptRef.current = true;
    const navigation = pendingNavigation;
    setPendingNavigation(null);
    navigation();
  };

  return (
    <div className={s.container}>
      <header className={s.header}>
        <div>
          <span className={s.eyebrow}>{company.name}</span>
          <h1 className={s.title}>
            {isEditMode ? "Edit Project" : "Create Project"}
          </h1>
          {isEditMode && projectTitle && (
            <span className={s.projectName}>{projectTitle}</span>
          )}
          <p className={s.subtitle}>
            Configure Project content, availability, rules, tags, and pricing
            for <span>{company.name}</span>.
          </p>
        </div>
        <div className={s.headerActions}>
          <button
            type="button"
            className={s.secondaryButton}
            onClick={() => requestGuardedNavigation(returnToProjects)}
          >
            Back to Projects
          </button>

          <StandartButton
            buttonTitle={
              controller.loading
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Create Project"
            }
            action={controller.saveProject}
            disabled={controller.loading}
          />
        </div>
      </header>

      <div className={s.formShell}>
        <div className={s.formContent}>
          {controller.errorMessage && (
            <div className={s.formAlert} role="alert">
              {controller.errorMessage}
            </div>
          )}

          {activeStep === "Content" && (
            <ContentStep
              controller={controller}
              selectedLanguage={selectedLanguage}
              onLanguageChange={setSelectedLanguage}
            />
          )}

          {activeStep === "Availability" && (
            <AvailabilityStep company={company} controller={controller} />
          )}

          {activeStep === "Specialist Scope" && (
            <SpecialistScopeStep controller={controller} />
          )}

          {activeStep === "Pricing" && (
            <PricingStep controller={controller} project={project} />
          )}

          {activeStep === "Rules" && <RulesStep controller={controller} />}

          {activeStep === "Courses" && (
            <CoursesStep
              controller={controller}
              project={project}
              token={token}
            />
          )}

          {activeStep === "Quiz" && (
            <QuizStep controller={controller} project={project} token={token} />
          )}

          {activeStep === "Tags" && <TagsStep controller={controller} />}

          {activeStep === "Review & Save" && (
            <ReviewStep
              company={company}
              controller={controller}
              mode={mode}
              project={project}
            />
          )}
        </div>

        <ProjectFormStepper
          activeStep={activeStep}
          onStepChange={setActiveStep}
        />
      </div>

      <ConfirmationModal
        confirmFunc={confirmPendingNavigation}
        confirmTitle="You have unsaved Project changes. Leave without saving?"
        modalVisible={Boolean(pendingNavigation)}
        setModalVisible={() => setPendingNavigation(null)}
      />
    </div>
  );
};

const ProjectFormPage = () => {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const route = useMemo(
    () => parseProjectFormRoute(location.pathname),
    [location.pathname],
  );
  const [loadState, setLoadState] = useState<LoadState>({
    loading: true,
    error: "",
    company: null,
    project: null,
  });

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      if (!route) {
        setLoadState({
          loading: false,
          error: "This Project form route is not supported.",
          company: null,
          project: null,
        });
        return;
      }

      if (!token) {
        setLoadState({
          loading: false,
          error: "Authentication token is missing. Please sign in again.",
          company: null,
          project: null,
        });
        return;
      }

      setLoadState({
        loading: true,
        error: "",
        company: null,
        project: null,
      });

      try {
        const companyResponse = (await CompanyApi.getCompanyById(
          token,
          route.companyId,
        )) as CompanyLoadResponse;

        if (ignore) return;

        const company = getCompanyFromResponse(companyResponse);

        if (!company) {
          setLoadState({
            loading: false,
            error:
              getResponseMessage(companyResponse) ||
              "Unable to load the company for this Project.",
            company: null,
            project: null,
          });
          return;
        }

        if (route.mode === "create") {
          setLoadState({
            loading: false,
            error: "",
            company,
            project: null,
          });
          return;
        }

        if (!route.projectId) {
          setLoadState({
            loading: false,
            error: "Project ID is missing from the edit route.",
            company,
            project: null,
          });
          return;
        }

        const projectResponse = (await ProjectApi.getProjectById(
          token,
          route.projectId,
        )) as ProjectLoadResponse;

        if (ignore) return;

        const project = getProjectFromResponse(projectResponse);

        if (!project) {
          setLoadState({
            loading: false,
            error:
              getResponseMessage(projectResponse) ||
              "Unable to load the Project for editing.",
            company,
            project: null,
          });
          return;
        }

        setLoadState({
          loading: false,
          error: "",
          company,
          project,
        });
      } catch (error) {
        if (ignore) return;
        console.error("Error loading Project form data:", error);
        setLoadState({
          loading: false,
          error: "Something went wrong while loading Project form data.",
          company: null,
          project: null,
        });
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [route, token]);

  if (loadState.loading) {
    return (
      <div className={s.container}>
        <div className={s.state}>
          <Loader size={64} />
          <p className={s.stateText}>Loading Project form...</p>
        </div>
      </div>
    );
  }

  if (loadState.error) {
    return (
      <div className={s.container}>
        <div className={s.state} role="alert">
          <h1 className={s.stateTitle}>Project form unavailable</h1>
          <p className={s.stateText}>{loadState.error}</p>
        </div>
      </div>
    );
  }

  return (
    <ProjectFormLoadedContent
      company={loadState.company!}
      mode={route?.mode ?? "create"}
      project={loadState.project}
      token={token}
    />
  );
};

export default ProjectFormPage;
